import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BarcodeScanner from '../components/BarcodeScanner';
import { productsApi, UpcLookupResult } from '../services/api';

export default function ScanUpc() {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [manualUpc, setManualUpc] = useState('');
  const [results, setResults] = useState<UpcLookupResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastScanned, setLastScanned] = useState<string | null>(null);

  const lookupUpc = async (upc: string) => {
    if (!upc.trim()) {
      setError('Please enter a UPC code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const products = await productsApi.lookupByUpc(upc.trim());
      setResults(products);
      if (products.length === 0) {
        setError(`No products found for UPC: ${upc}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to lookup UPC');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScan = (decodedText: string) => {
    // One-shot: stop scanning, run lookup. User taps "Start" again to scan another.
    setScanning(false);
    setLastScanned(decodedText);
    lookupUpc(decodedText);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLastScanned(manualUpc);
    lookupUpc(manualUpc);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
          >
            <svg className="h-6 w-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-slate-800">Scan UPC</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Scanner - kept mounted so camera stays acquired between scans.
            Hidden via a wrapper before the user taps "Start" the first time
            so we don't request camera permission until they ask for it. */}
        <div className="rounded-xl bg-white shadow-card overflow-hidden">
          <div className="flex justify-center bg-slate-900 p-3">
            {scanning || lastScanned ? (
              <BarcodeScanner
                isActive={scanning}
                onScan={handleScan}
                onError={(err) => setError(err)}
                width={Math.min(window.innerWidth - 64, 350)}
                height={280}
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-white py-8">
                <svg className="h-12 w-12 mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                <p className="text-sm text-slate-400">Tap below to start scanning</p>
              </div>
            )}
          </div>

          <div className="p-4">
            {scanning ? (
              <button
                onClick={() => setScanning(false)}
                className="w-full rounded-xl bg-red-600 py-3 font-semibold text-white hover:bg-red-700"
              >
                Stop Scanner
              </button>
            ) : (
              <button
                onClick={() => {
                  setError(null);
                  setScanning(true);
                }}
                className="w-full rounded-xl bg-primary-600 py-3 font-semibold text-white hover:bg-primary-700"
              >
                {lastScanned ? 'Scan Another' : 'Start Camera Scanner'}
              </button>
            )}
          </div>
        </div>

        {/* Manual Entry */}
        <div className="rounded-xl bg-white p-4 shadow-card">
          <h3 className="text-sm font-medium text-slate-500 mb-3">Or Enter UPC Manually</h3>
          <form onSubmit={handleManualSubmit} className="flex gap-2">
            <input
              type="text"
              value={manualUpc}
              onChange={(e) => setManualUpc(e.target.value)}
              placeholder="Enter UPC code..."
              className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-slate-800 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
            <button
              type="submit"
              disabled={isLoading || !manualUpc.trim()}
              className="rounded-lg bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700 disabled:bg-slate-300"
            >
              {isLoading ? 'Looking...' : 'Look Up'}
            </button>
          </form>
        </div>

        {/* Last Scanned */}
        {lastScanned && (
          <div className="rounded-xl bg-slate-100 px-4 py-2 text-sm text-slate-600">
            Last scanned: <span className="font-mono font-medium">{lastScanned}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-xl bg-red-50 p-4 text-red-700">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-slate-500">
              Found {results.length} product{results.length !== 1 ? 's' : ''}
            </h3>

            {results.map((product) => (
              <div key={product.productid} className="rounded-xl bg-white p-4 shadow-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-800 truncate">{product.description}</h4>

                    {product.brand && (
                      <p className="text-sm text-primary-600 font-medium mt-0.5">{product.brand}</p>
                    )}

                    <div className="mt-2 space-y-1 text-sm text-slate-600">
                      <div className="flex gap-4">
                        {product.upc && (
                          <span className="font-mono">UPC: {product.upc}</span>
                        )}
                        {product.codenbr && (
                          <span className="font-mono">Code: {product.codenbr}</span>
                        )}
                      </div>

                      <div className="flex gap-4">
                        {product.packsize && <span>Pack: {product.packsize}</span>}
                        {product.size && <span>Size: {product.size}</span>}
                        {product.uom && <span>UOM: {product.uom}</span>}
                      </div>
                    </div>
                  </div>

                  {product.cost && (
                    <div className="text-right">
                      <span className="text-lg font-bold text-green-600">
                        ${product.cost.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Relationships */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                  {product.distributor && (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                      <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      {product.distributor.name}
                    </span>
                  )}
                  {product.retailer && (
                    <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700">
                      <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      {product.retailer.name}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && results.length === 0 && !error && lastScanned && (
          <div className="rounded-xl bg-slate-100 p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 text-slate-600">No products found</p>
            <p className="text-sm text-slate-500">Try scanning a different barcode</p>
          </div>
        )}

        {/* Instructions */}
        {!lastScanned && !scanning && (
          <div className="rounded-xl bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 flex-shrink-0 mt-0.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-700">
                <p className="font-medium">How to use:</p>
                <ol className="mt-1 list-decimal list-inside space-y-1">
                  <li>Tap "Start Camera Scanner" to scan a barcode</li>
                  <li>Point your camera at any UPC or shelf-tag barcode</li>
                  <li>Or type the UPC manually and tap "Look Up"</li>
                  <li>View all matching products and their relationships</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
